import { ButtonBase } from '@material-ui/core';
import moment from 'moment';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Inputs,
  PaginationComponent,
  Spinner,
  Tables,
} from '../../../../../../Components';
import { TableActions } from '../../../../../../Enums';
import { GetParams, showError } from '../../../../../../Helper';
import { GetUserId } from '../../../../../../Services';
import { GetAllAwards } from '../../../../../../Services/AwardServices';
import CreateNewUserAwardDialog from './UI/CreateNewUserAwardDialog';
import DeleteUserAward from './UI/DeleteUserAward';
import EditUserAward from './UI/EditUserAward';
import ViewAwardDialog from './UI/ViewAwardDialog';
import './UserAwardsSection.scss';
 
function UserAwardsSection({ translationPath }) {
  const { t } = useTranslation(translationPath);
  const reducer = useCallback((state, action) => {
    if (action.id !== 'edit') return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [activeAward, setActiveAward] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const userSession = JSON.parse(localStorage.getItem('session'));
 
  const [filter, setFilter] = useReducer(reducer, {
    pageIndex: 1,
    pageSize: parseInt(localStorage.getItem('Pagination')) || 25,
    awardName: null,
  });
 
  async function getAllUserAwards() {
    setIsLoading(true);
    const body = {
      ...filter,
      userId: userId,
    };
    const res = await GetAllAwards(body);
 
    if (!(res && res.status && res.status !== 200)) {
      setData(res || []);
      setIsLoading(false);
    } else {
      setData([]);
      showError(t('error-fetching-user-awards'));
      setIsLoading(false);
    }
  }
 
  async function getUserIdForCurrentUser() {
    const URLUserId = GetParams('id');
    const res = await GetUserId(URLUserId);
    if (!(res && res.status && res.status !== 200)) {
      setUserId(res);
    } else {
      setUserId(null);
    }
  }
 
  function tableActionClicked(actionEnum, item, focusedRow, event) {
    event.preventDefault();
    if (actionEnum === TableActions.view.key) {
      setIsViewDialogOpen(true);
    } else if (actionEnum === TableActions.edit.key) {
      setIsEditDialogOpen(true);
    } else if (actionEnum === TableActions.delete.key) {
      setIsDeleteDialogOpen(true);
    }
  }
 
  const onPageIndexChanged = (pageIndex) => {
    setFilter({ id: 'pageIndex', value: pageIndex });
  };
  const onPageSizeChanged = (pageSize) => {
    setFilter({
      id: 'edit',
      value: {
        awardName: filter.awardName,
        pageSize: pageSize,
        pageIndex: 1,
      },
    });
  };
 
  function refetchData() {
    getAllUserAwards();
  }
 
  useEffect(() => {
    getUserIdForCurrentUser();
  }, []);
 
  useEffect(() => {
    if (userId) {
      getAllUserAwards();
    }
  }, [userId, filter]);
 
  return (
    <>
      <Spinner isActive={isLoading} isAbsolute />
      <div className='user-awards-section-wrapper'>
        <p className='user-awards-title'>{t('user-awards')}</p>
 
        <div className='d-flex'>
          <Inputs
            idRef={'awardNameSearchRef'}
            inputPlaceholder={'search-award-name'}
            type={'text'}
            value={filter.awardName || ''}
            onInputChanged={(e) => {
              const value = e.target.value ?? null;
              setFilter({ id: 'awardName', value: value });
            }}
            translationPath={''}
            parentTranslationPath={translationPath}
          />
 
          <ButtonBase
            className='MuiButtonBase-root btns theme-solid '
            style={{ maxHeight: '28px' }}
            onClick={() => {
              setIsCreateDialogOpen(true);
            }}
          >
            {t('create')}
          </ButtonBase>
        </div>
        <Tables
          data={data.result || []}
          headerData={[
            {
              id: 1,
              label: 'award-name',
              input: 'awardName',
            },
            {
              id: 2,
              label: 'award-date',
              component: (item) => (
                <>
                  <span>
                    {moment(item.awardDate).format('DD/MM/YYYY - hh:mm A') ||
                      'N/A'}
                  </span>
                </>
              ),
            },
               {
              id: 3,
              label: 'Granted To',
              input: 'grantedTo',
            },
          ]}
          activePage={0}
          itemsPerPage={filter.pageSize || 0}
          defaultActions={[
            { enum: TableActions.view.key },
            { enum: TableActions.edit.key },
            { enum: TableActions.delete.key },
          ]}
          actionsOptions={{
            onActionClicked: tableActionClicked,
          }}
          focusedRowChanged={(rowIndex, item) => {
            setActiveAward(item);
          }}
          totalItems={data.totalCount || 0}
          translationPath={''}
          parentTranslationPath={translationPath}
        />
        <PaginationComponent
          pageIndex={filter?.pageIndex - 1}
          pageSize={filter?.pageSize}
          totalCount={data?.totalCount || 0}
          onPageIndexChanged={onPageIndexChanged}
          onPageSizeChanged={onPageSizeChanged}
        />
      </div>
 
      {isCreateDialogOpen && (
        <CreateNewUserAwardDialog
          isOpen={isCreateDialogOpen}
          userSession={userSession}
          userId={userId}
          refetchData={refetchData}
          translationPath={translationPath}
          setIsCreateDialogOpen={setIsCreateDialogOpen}
        />
      )}
      {isEditDialogOpen && (
        <EditUserAward
          isOpen={isEditDialogOpen}
          translationPath={translationPath}
          setIsEditDialogOpen={setIsEditDialogOpen}
          activeAward={activeAward}
          refetchData={refetchData}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteUserAward
          isOpen={isDeleteDialogOpen}
          setIsDeleteDialogOpen={setIsDeleteDialogOpen}
          translationPath={translationPath}
          activeAward={activeAward}
          refetchData={refetchData}
        />
      )}
      {isViewDialogOpen && (
        <ViewAwardDialog
          isOpen={isViewDialogOpen}
          activeAward={activeAward}
          translationPath={translationPath}
          setIsViewDialogOpen={setIsViewDialogOpen}
        />
      )}
    </>
  );
}
 
export default UserAwardsSection;